import { useRef, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PenLine, RotateCcw, CheckCircle2 } from 'lucide-react';
import SignaturePad from 'signature_pad';
import { onsiteApiFetch } from '../config/api.js';
import type { OnsiteJob } from '../../../api/src/types/onsite-types.js';

export default function SignaturePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePad | null>(null);
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [isEmpty, setIsEmpty] = useState(true);

  const { data } = useQuery({
    queryKey: ['job', id],
    queryFn: () => onsiteApiFetch<{ job: OnsiteJob }>(`/api/v1/onsite/jobs/${id}`),
    enabled: !!id,
  });

  const completeJob = useMutation({
    mutationFn: async ({ signatureDataUrl }: { signatureDataUrl: string }) => {
      // Save signature
      await onsiteApiFetch('/api/v1/onsite/signatures', {
        method: 'POST',
        body: JSON.stringify({
          job_id: id,
          signer_name: signerName,
          signer_title: signerTitle || undefined,
          signature_data_url: signatureDataUrl,
        }),
      });
      // Complete the job
      return onsiteApiFetch(`/api/v1/onsite/jobs/${id}/complete`, {
        method: 'POST',
        body: JSON.stringify({ resolution_notes: data?.job.notes ?? '' }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['job', id] });
      navigate(`/jobs/${id}`, { replace: true });
    },
  });

  useEffect(() => {
    if (!canvasRef.current) return;
    padRef.current = new SignaturePad(canvasRef.current, {
      backgroundColor: 'rgba(15, 23, 42, 1)',
      penColor: '#f1f5f9',
      minWidth: 2,
      maxWidth: 4,
    });
    padRef.current.addEventListener('afterUpdateStroke', () => {
      setIsEmpty(padRef.current?.isEmpty() ?? true);
    });

    // Resize canvas to match display
    const resize = () => {
      const canvas = canvasRef.current;
      if (!canvas || !padRef.current) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d')?.scale(ratio, ratio);
      padRef.current.clear();
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  async function handleSubmit() {
    if (!padRef.current || padRef.current.isEmpty()) return;
    const dataUrl = padRef.current.toDataURL('image/png');
    await completeJob.mutateAsync({ signatureDataUrl: dataUrl });
  }

  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-foreground">Customer Sign-Off</h1>
        {data?.job && (
          <p className="text-sm text-muted-foreground">{data.job.title} — {data.job.customer_name}</p>
        )}
      </div>

      {/* Signer info */}
      <div className="mb-4 grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            Customer Name <span className="text-red-400">*</span>
          </label>
          <input
            value={signerName}
            onChange={e => setSignerName(e.target.value)}
            placeholder="Full name"
            className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Title / Role</label>
          <input
            value={signerTitle}
            onChange={e => setSignerTitle(e.target.value)}
            placeholder="e.g. IT Manager"
            className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
          />
        </div>
      </div>

      {/* Signature canvas */}
      <div className="flex-1 overflow-hidden rounded-xl border-2 border-dashed border-border bg-background">
        <canvas
          ref={canvasRef}
          className="h-full w-full touch-none"
          style={{ touchAction: 'none' }}
        />
      </div>

      {isEmpty && (
        <p className="mt-2 text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
          <PenLine className="h-3.5 w-3.5" />
          Sign in the box above
        </p>
      )}

      {/* Actions */}
      <div className="mt-4 flex gap-3">
        <button
          onClick={() => { padRef.current?.clear(); setIsEmpty(true); }}
          className="flex items-center gap-2 rounded-xl border border-border px-4 py-3 text-sm text-muted-foreground hover:bg-secondary"
        >
          <RotateCcw className="h-4 w-4" />
          Clear
        </button>
        <button
          onClick={handleSubmit}
          disabled={isEmpty || !signerName.trim() || completeJob.isPending}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-3 font-semibold text-foreground hover:bg-green-700 disabled:opacity-50"
        >
          {completeJob.isPending ? (
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            <>
              <CheckCircle2 className="h-5 w-5" />
              Complete Job
            </>
          )}
        </button>
      </div>
    </div>
  );
}
